import { useEffect, useState } from 'react'
import { Lock, ShieldCheck, ChevronLeft, CircleCheck, CircleX, Download } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { apiGet, adminMutate, useToast } from './primitives.js'
import { invalidateAccessCaps } from '../access/lib.js'
import { useConfirm } from '../shell/confirm.js'
import { CleAccesPanel } from './CleAccesPanel.js'

/**
 * ADMINISTRATION → INTÉGRATIONS → UniFi Access — fiche complète : ÉTAT ·
 * MODE SÛR · CONNEXION · SCOPES · COUVERTURE API · DIAGNOSTIC. Passage en
 * Édition = action sensible (step-up). Ce lot livre l'intégration en LECTURE
 * SEULE et ne la passe jamais en Édition automatiquement.
 */

interface Station { id: string; name: string; os: string | null; version: string | null; reader: string | null; createdAt: string; lastSeen: string | null; online: boolean; revoked: boolean }
interface CompanionManifest { available: boolean; version?: string; file?: string; sha256?: string; size?: number }
interface Operation { category: string; ref: string; label: string; method: string; path: string; kind: 'READ' | 'WRITE'; providerScope: string; action?: string; permission?: string; ui?: string; status: string; reason?: string }
interface ApiOperability { totalOperations: number; reads: number; writes: number; operable: number; readOperable: number; deferred: number; unsupported: number; notRelevant: number; forbidden: number; unexplainedGaps: number }
interface Fiche {
  name: string; baseUrl: string; status: string; enabled: boolean
  configuredMode: string; effectiveMode: string; maxMode: string; globalMode: string
  limitingFactor: 'GLOBAL_MODE' | 'INTEGRATION' | 'CAPABILITY' | 'ENVIRONMENT' | null
  safeModeLabel: string; editionAvailable: boolean; automationAvailable: boolean; tokenConfigured: boolean
  freshness: { mode: string; modeLabel: string; lastSync: string | null; stale: boolean }
  syncState: { resource: string; lastSuccess: string | null; error: string | null }[]
  operations: Operation[]
  apiOperability: ApiOperability
  operability?: { operable: number; total: number; deferred: number }
}

const MODE_LABELS: Record<string, string> = { DISABLED: 'Désactivée', READ_ONLY: 'Lecture seule', EDIT: 'Édition', AUTOMATION: 'Automatisations' }
const FACTEUR: Record<string, string> = { GLOBAL_MODE: 'le Mode sûr global', INTEGRATION: 'la configuration de l’intégration', CAPABILITY: 'les capacités de l’intégration', ENVIRONMENT: 'le plafond d’environnement' }
const MODES_SELECT = ['DISABLED', 'READ_ONLY', 'EDIT', 'AUTOMATION'] as const

/** « il y a 3 s / il y a 2 min / date » — l'état online reste piloté serveur. */
function relSeen(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `il y a ${s} s`
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  return new Date(iso).toLocaleString('fr-FR')
}

export function AccessIntegrationPage() {
  const meta = useShellMeta()
  const toast = useToast()
  const confirm = useConfirm()
  const [f, setF] = useState<Fiche | null>(null)
  const load = () => void apiGet<Fiche>('/api/v1/access/integration').then(setF).catch(() => {})
  useEffect(load, [])

  /* ── STATIONS NFC Companion V1 (§35-44) — téléchargement, appairage, test ── */
  const [stations, setStations] = useState<Station[]>([])
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null)
  const [manifest, setManifest] = useState<CompanionManifest | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [testState, setTestState] = useState<{ station: Station; status: 'waiting' | 'detected' | 'failed'; uid?: string; error?: string; sessionId?: string } | null>(null)
  const loadStations = () => void apiGet<{ stations: Station[] }>('/api/v1/access/nfc-stations').then((r) => setStations(r.stations ?? [])).catch(() => {})
  useEffect(() => {
    loadStations()
    void apiGet<CompanionManifest>('/api/v1/access/nfc-companion/manifest').then(setManifest).catch(() => {})
    const t = setInterval(loadStations, 5000) // online/offline vivant (§38-39)
    return () => clearInterval(t)
  }, [])

  /** TESTER (§33-34) : session TEST — lecture d'UID, AUCUNE écriture provider. */
  const testStation = async (st: Station) => {
    setTestState({ station: st, status: 'waiting' })
    const c = await adminMutate<{ sessionId?: string; expiresAt?: string; message?: string; error?: string }>('POST', '/api/v1/access/nfc-sessions', { stationId: st.id, purpose: 'TEST' })
    if (c.status >= 400 || !c.data.sessionId) { setTestState({ station: st, status: 'failed', error: c.data.message ?? c.data.error ?? 'Session refusée.' }); return }
    const sid = c.data.sessionId
    setTestState({ station: st, status: 'waiting', sessionId: sid })
    const deadline = Date.now() + 125_000
    while (Date.now() < deadline) {
      await new Promise((res) => setTimeout(res, 900))
      const r = await apiGet<{ status: string; uid: string | null }>(`/api/v1/access/nfc-sessions/${sid}`)
      if (r.status === 'DETECTED' && r.uid) { setTestState({ station: st, status: 'detected', uid: (r.uid.match(/.{2}/g) ?? [r.uid]).join(' ') }); return }
      if (r.status === 'EXPIRED' || r.status === 'CANCELLED') { setTestState({ station: st, status: 'failed', error: 'Aucune carte présentée.' }); return }
    }
    setTestState({ station: st, status: 'failed', error: 'Expiré.' })
  }
  const closeTest = async () => {
    if (testState?.sessionId && testState.status === 'waiting') await adminMutate('DELETE', `/api/v1/access/nfc-sessions/${testState.sessionId}`, {})
    setTestState(null)
  }
  const rename = async () => {
    if (!renaming) return
    const r = await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/access/nfc-stations/${renaming.id}`, { name: renaming.name })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    setRenaming(null); loadStations()
  }
  const genCode = async () => {
    const r = await adminMutate<{ code?: string; expiresAt?: string; message?: string; error?: string }>('POST', '/api/v1/access/nfc-stations/pairing-code', {})
    if (r.status >= 400 || !r.data.code) { toast('error', r.data.message ?? r.data.error ?? 'Refusé.'); return }
    setPairing({ code: r.data.code, expiresAt: r.data.expiresAt! })
  }
  const revoke = async (st: Station) => {
    if (!(await confirm({ title: `Révoquer la station « ${st.name} » ?`, message: 'Son jeton est invalidé immédiatement : elle ne pourra plus remonter d’UID. Ré-appairage nécessaire pour la réutiliser.', variant: 'sensitive', confirmLabel: 'Révoquer' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/access/nfc-stations/${st.id}`, {})
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Station révoquée — jeton invalidé.')
    loadStations()
  }

  const changeMode = async (mode: string) => {
    if (mode === 'EDIT' && !(await confirm({ title: 'Passer UniFi Access en Édition ?', message: 'Les opérateurs autorisés pourront modifier UniFi Access depuis Gestion (une fois le Mode sûr global en Édition).', variant: 'sensitive', confirmLabel: 'Passer en Édition' }))) return
    if (mode === 'DISABLED' && !(await confirm({ title: 'Désactiver UniFi Access ?', message: 'Les nouvelles données ne seront plus synchronisées. L’historique local est conservé.', variant: 'sensitive', confirmLabel: 'Désactiver' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/access/integration/safe-mode', { mode })
    if (r.status >= 400) { toast('error', r.data.message ?? r.data.error ?? 'Refusé.'); return }
    toast('ok', `Mode configuré : ${MODE_LABELS[mode]}`)
    // Les pages Access ouvertes doivent refléter le nouveau mode sans reload (§10).
    invalidateAccessCaps()
    load()
  }

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ai-page">
        <Link className="lc-crumb" href="/settings/integrations"><ChevronLeft size={15} aria-hidden="true" /> Intégrations</Link>
        {f ? (
          <>
            <header className="ai-head">
              <div>
                <h1 className="lc-title">{f.name}</h1>
                {/* Le badge principal = mode EFFECTIF (§18). */}
                <p className="lc-subtitle"><span className="lc-pill lc-pill--ok">{f.status}</span> <span className="ai-mode-pill"><Lock size={12} aria-hidden="true" /> {MODE_LABELS[f.effectiveMode]}</span> · Développeur API, réseau interne, jeton dédié.</p>
              </div>
            </header>

            <div className="ai-grid">
              <section className="ai-panel">
                <h2 className="lc-section-title">Mode sûr</h2>
                <p className="lc-hint">Le mode <strong>configuré</strong> de cette intégration. Le mode <strong>effectif</strong> réel est le plus restrictif entre lui, le Mode sûr global et les capacités.</p>
                {/* Le sélecteur montre le CONFIGURÉ, jamais l'effectif (§18, §21). */}
                <div className="ai-modes">
                  {MODES_SELECT.map((m) => {
                    const disabled = (m === 'EDIT' && !f.editionAvailable) || (m === 'AUTOMATION' && !f.automationAvailable)
                    return <button key={m} type="button" className={`ai-mode ${f.configuredMode === m ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''}`} disabled={disabled} title={m === 'AUTOMATION' && !f.automationAvailable ? 'Non disponible : UniFi Access ne supporte pas les automatisations d’écriture.' : undefined} onClick={() => changeMode(m)}>{MODE_LABELS[m]}</button>
                  })}
                </div>
                <dl className="ai-modestate">
                  <div><dt>Configuré</dt><dd><span className="ai-mode-pill">{MODE_LABELS[f.configuredMode]}</span></dd></div>
                  <div><dt>Effectif</dt><dd><span className={`ai-mode-pill ${f.effectiveMode === 'EDIT' || f.effectiveMode === 'AUTOMATION' ? 'ai-mode-pill--edit' : ''}`}>{MODE_LABELS[f.effectiveMode]}</span></dd></div>
                  <div><dt>Maximum supporté</dt><dd>{MODE_LABELS[f.maxMode]}</dd></div>
                  {f.limitingFactor ? <div><dt>Limité par</dt><dd className="ai-limited">{FACTEUR[f.limitingFactor]}</dd></div> : null}
                </dl>
                {f.limitingFactor === 'GLOBAL_MODE' ? (
                  <p className="ai-explain">UniFi Access est configuré pour autoriser les modifications, mais le Mode sûr global limite actuellement toutes les intégrations. <Link href="/settings/general#safe-mode" className="ai-globallink">Modifier le Mode sûr global →</Link></p>
                ) : null}
                <p className="ai-automation"><Lock size={13} aria-hidden="true" /> Automatisations : <strong>Non disponibles</strong> — UniFi Access n’a aucune automatisation d’écriture. Maximum : Édition.</p>
              </section>

              <section className="ai-panel">
                <h2 className="lc-section-title">Connexion</h2>
                <dl className="ac-dl">
                  <div><dt>URL du service (réseau interne)</dt><dd className="ac-mono">{f.baseUrl}</dd></div>
                  <div><dt>Jeton d’API</dt><dd>{f.tokenConfigured ? 'Configuré — jamais réaffiché ; se remplace dans « Clé d’accès » ci-dessous' : 'Non configuré — voir « Clé d’accès » ci-dessous'}</dd></div>
                  <div><dt>Dernière synchronisation</dt><dd>{f.freshness.lastSync ? new Date(f.freshness.lastSync).toLocaleString('fr-FR') : '—'}{f.freshness.stale ? ' · possiblement ancienne' : ''}</dd></div>
                </dl>
                <p className="lc-hint">Le jeton ne quitte jamais le serveur. Le navigateur ne contacte jamais UniFi Access.</p>
              </section>

              {/* Clé d'accès (15/10/2026) : jusqu'ici le jeton ne se changeait que
                  par la base. Même panneau que les autres intégrations. */}
              <div className="ai-panel--wide"><CleAccesPanel integration="unifi_access" onEnregistree={load} /></div>

              <section className="ai-panel ai-panel--wide">
                <div className="ai-cov-head"><h2 className="lc-section-title">Stations NFC — Gestion NFC Companion</h2>
                  <button type="button" className="lc-btn" onClick={() => void genCode()}>Générer un code d’appairage</button>
                </div>

                {/* TÉLÉCHARGEMENT (§36-37, §113-115) : le vrai dernier artefact approuvé. */}
                <div className="ai-companion">
                  <div className="ai-companion__dl">
                    {manifest?.available ? (
                      <>
                        <a className="lc-btn lc-btn--primary ai-companion__btn" href="/api/v1/access/nfc-companion/download">
                          <Download size={15} aria-hidden="true" /> Télécharger Gestion NFC Companion
                        </a>
                        <span className="lc-hint">Windows x64 · version {manifest.version} · {Math.round((manifest.size ?? 0) / 1_000_000)} Mo · application interne non signée</span>
                        <span className="lc-hint ai-companion__sha">SHA-256 : <code>{manifest.sha256}</code></span>
                      </>
                    ) : <span className="lc-hint">Aucun artefact Companion publié pour le moment.</span>}
                  </div>
                  <ol className="ai-companion__steps">
                    <li><strong>Installer sur un nouveau PC</strong></li>
                    <li>Télécharger puis installer (double-clic sur le MSI)</li>
                    <li>Générer un code d’appairage ci-dessus</li>
                    <li>Saisir le code dans Companion au premier lancement</li>
                    <li>Terminé — la station apparaît ci-dessous</li>
                  </ol>
                </div>
                {pairing ? (
                  <p className="ai-pairing">Code d’appairage : <strong className="ac-mono ai-pairing__code">{pairing.code}</strong> · valable jusqu’à {new Date(pairing.expiresAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · usage unique.</p>
                ) : null}

                {/* CARTES STATIONS (§38-43) : état RÉEL (en ligne = vue < 90 s), jamais de faux online. */}
                <div className="ai-stationgrid">
                  {stations.filter((st) => !st.revoked).map((st) => (
                    <div key={st.id} className={`ai-station ${st.online ? '' : 'ai-station--off'}`}>
                      <div className="ai-station__head">
                        <span className={`lc-dot ${st.online ? 'lc-dot--ok lc-dot--live' : 'lc-dot--idle'}`} aria-hidden="true" />
                        {renaming?.id === st.id ? (
                          <span className="ai-station__rename">
                            <input value={renaming.name} onChange={(e) => setRenaming({ id: st.id, name: e.target.value })} aria-label="Nouveau nom" />
                            <button type="button" className="ac-inline-link" onClick={() => void rename()}>OK</button>
                            <button type="button" className="ac-inline-link" onClick={() => setRenaming(null)}>Annuler</button>
                          </span>
                        ) : <strong>{st.name}</strong>}
                        <span className={`lc-pill ${st.online ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{st.online ? 'En ligne' : 'Hors ligne'}</span>
                      </div>
                      <dl className="ai-station__facts">
                        <div><dt>Système</dt><dd>{st.os ?? '—'}</dd></div>
                        <div><dt>Companion</dt><dd className="ac-mono">{st.version ?? '—'}{manifest?.available && st.version && manifest.version !== st.version ? <span className="lc-pill lc-pill--warning ai-station__update">Mise à jour disponible</span> : null}</dd></div>
                        <div><dt>Lecteur</dt><dd>{st.reader ?? '—'}</dd></div>
                        <div><dt>Dernière connexion</dt><dd>{st.lastSeen ? relSeen(st.lastSeen) : 'jamais'}</dd></div>
                      </dl>
                      <div className="ai-station__actions">
                        <button type="button" className="lc-btn" disabled={!st.online} onClick={() => void testStation(st)}>Tester</button>
                        <button type="button" className="lc-btn" onClick={() => setRenaming({ id: st.id, name: st.name })}>Renommer</button>
                        <button type="button" className="lc-btn" onClick={() => void revoke(st)}>Révoquer</button>
                      </div>
                    </div>
                  ))}
                  {stations.filter((st) => !st.revoked).length === 0 ? (
                    <p className="lc-hint">Aucune station appairée. Installer Companion sur un PC puis générer un code.</p>
                  ) : null}
                </div>
                <p className="lc-hint">Une station ne détient <strong>jamais</strong> de jeton UniFi Access — jeton Gestion dédié, haché en base, révocable ici (effet immédiat).</p>

                {/* MODE TEST (§34) : lecture d'UID — AUCUNE MODIFICATION EFFECTUÉE. */}
                {testState ? (
                  <div className="ai-testbox">
                    {testState.status === 'waiting' ? (
                      <><strong>Test — {testState.station.name}</strong>
                        <p>Lecteur connecté ✓ — présentez une carte sur {testState.station.reader ?? 'le lecteur'}…</p></>
                    ) : testState.status === 'detected' ? (
                      <><strong>Carte détectée ✓</strong>
                        <p className="ac-mono">UID : {testState.uid}</p>
                        <p className="lc-hint">AUCUNE MODIFICATION EFFECTUÉE — ni dans Gestion, ni dans UniFi Access.</p></>
                    ) : (
                      <><strong>Test — {testState.station.name}</strong><p className="ac-err">{testState.error}</p></>
                    )}
                    <button type="button" className="lc-btn" onClick={() => void closeTest()}>Fermer</button>
                  </div>
                ) : null}
              </section>

              <section className="ai-panel ai-panel--wide">
                <div className="ai-cov-head"><h2 className="lc-section-title">Couverture API & opérabilité (catalogue d’opérations)</h2>
                  <span className="lc-hint">
                    <strong>API OPERABILITY : {f.apiOperability.totalOperations} opérations officielles inventoriées · {f.apiOperability.writes} écritures dont {f.apiOperability.operable} OPÉRABLES · {f.apiOperability.readOperable} lectures servies</strong>
                    {' '}· {f.apiOperability.deferred} différée(s) · {f.apiOperability.unsupported} non supportée(s) provider · {f.apiOperability.notRelevant} sans objet · {f.apiOperability.forbidden} interdite(s) (webhook MC) · <strong>{f.apiOperability.unexplainedGaps} trou inexpliqué</strong>
                  </span>
                </div>
                <table className="ac-table ai-coverage ai-ops">
                  <thead><tr><th>Catégorie</th><th>Opération</th><th>Méthode · Chemin</th><th>Scope provider</th><th>Permission Gestion</th><th>UI</th><th>État</th></tr></thead>
                  <tbody>
                    {f.operations.map((o, i) => (
                      <tr key={i}>
                        <td>{o.category}</td>
                        <td>{o.label}</td>
                        <td className="ac-mono ai-cov-note">{o.method} {o.path}</td>
                        <td className="ac-mono ai-cov-note">{o.providerScope}</td>
                        <td className="ac-mono ai-cov-note">{o.permission ?? '—'}</td>
                        <td className="ai-cov-note">{o.ui ?? '—'}</td>
                        <td>
                          {o.status === 'OPERABLE' ? <span className="lc-pill lc-pill--ok"><CircleCheck size={11} aria-hidden="true" /> Opérable</span>
                            : o.status === 'READ_OPERABLE' ? <span className="lc-pill lc-pill--ok">Lecture servie</span>
                            : o.status === 'DEFERRED' ? <span className="lc-pill lc-pill--idle" title={o.reason}>Différée</span>
                            : o.status === 'UNSUPPORTED_PROVIDER' ? <span className="lc-pill lc-pill--idle" title={o.reason}><CircleX size={11} aria-hidden="true" /> Provider</span>
                            : o.status === 'FORBIDDEN' ? <span className="lc-pill lc-pill--warning" title={o.reason}><Lock size={11} aria-hidden="true" /> Interdite</span>
                            : <span className="lc-pill lc-pill--idle" title={o.reason}>Sans objet</span>}
                          {o.reason ? <div className="ai-cov-note">{o.reason}</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section className="ai-panel ai-panel--wide">
                <h2 className="lc-section-title">Diagnostic de synchronisation</h2>
                <div className="ai-sync-grid">
                  {f.syncState.map((s) => (
                    <div key={s.resource} className={`ai-sync ${s.error ? 'ai-sync--err' : 'ai-sync--ok'}`}>
                      {s.error ? <CircleX size={14} aria-hidden="true" /> : <CircleCheck size={14} aria-hidden="true" />}
                      <strong>{s.resource}</strong>
                      <span>{s.lastSuccess ? new Date(s.lastSuccess).toLocaleTimeString('fr-FR') : '—'}</span>
                    </div>
                  ))}
                </div>
                <p className="lc-hint ai-cert"><ShieldCheck size={13} aria-hidden="true" /> Cahier des charges : <code>docs/integrations/unifi-access-live-api-capability-matrix.md</code>. Webhook « Main courante LCR » présent et INTOUCHÉ.</p>
              </section>
            </div>
          </>
        ) : <p className="lc-hint">Chargement…</p>}
      </main>
    </Shell>
  )
}
